import type { JSX, ReactNode } from 'react';
import { Icon } from './Icon';
import type { IconName } from './Icon';
import { cx } from './cx';

/**
 * "Something is wrong with this device's connection to us" — a leading glyph on
 * an error-container fill, with whatever the screen needs to say beside it.
 *
 * Two screens raise one: `NoInternetBanner` for a device with no network, and
 * the key-entry gate for a key the backend has rejected. They were built
 * separately and ended up **identical** — the same flex row, the same padding,
 * the same radius, the same fill, down to the eight declarations `.no-internet`
 * and `.gate-error` each spelled out in `components.css`.
 *
 * That the two agreed was luck, and losing the agreement would be worse than it
 * sounds: giving the rarer one a louder style makes a stopped key read as a
 * different *class* of problem than an unplugged network, when they are the same
 * class — the device cannot report, and somebody has to go and look at it.
 *
 * ### The colour pairing is a contrast decision, not a palette choice
 *
 * `--error` on `--error-container` is 3.7:1. That clears the 3:1 WCAG 1.4.11
 * asks of a non-text element and misses the 4.5:1 a word needs, so `--error` is
 * spent on the glyph and the words beside it are `--error-container-text`. This
 * component owns the glyph, which is why the icon's class is not left to the
 * caller; the caller owns the words, and `components.css` is where the rule that
 * governs them is written down.
 *
 * ### The caller supplies the text, not a string
 *
 * The two banners do not say their piece the same way — one is a single
 * sentence, the other a title over a body — and flattening that into an optional
 * `title` prop would encode the difference here rather than at the two places
 * that actually have an opinion about it. What must not differ is the frame, and
 * the frame is all this owns.
 */
export function AlertBanner({
  icon,
  className,
  children,
}: {
  icon: IconName;
  /**
   * Extra classes for the banner itself — spacing that belongs to the parent's
   * arrangement rather than to the banner. The gate passes `gate-error`, which
   * is the bottom margin that separates the banner from the field underneath it;
   * the offline banner passes nothing, because its own wrapper (`.gate__banner`)
   * carries that on the one screen where it needs it.
   */
  className?: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <div className={cx('no-internet', className)}>
      <Icon name={icon} size={18} className="no-internet__icon" />
      {children}
    </div>
  );
}
